import * as swcHelpers from '@swc/helpers';
import React__default from 'react';
import { DataLayer, CartV2Actions } from '@grandvisionhq/analytics';
import { useMutation } from '@grandvisionhq/graphql';
import { useIntl, useFeatureToggles } from '@grandvisionhq/state';
import { monetate } from '@grandvisionhq/svc-monetate';
import { isNotNull } from '@grandvisionhq/utils';
import { errorsMapping } from '../mappers/error-codes.js';
import { mapDraftLineItemToServer } from '../mappers/server/line-item.js';
import { dispatchMiniCartEvent } from '../modules/MiniCart/utils/event.js';
import { useCartStorage, CART_STORAGE_KEY } from './use-cart-storage.js';
import { useCartSettings } from '../context/CartSettings/context.js';
import { appendSanitizationItems } from '../utils/append-sanitization-items.js';
import { getSolvableValidationErrors } from '../utils/get-solvable-validation-errors.js';
import { getValidationErrorCodes } from '../utils/get-validation-error-codes.js';
import { getAddedLineItems } from '../utils/get-added-line-items.js';
import { lineItemDataMapper } from '../utils/line-item-data-mapper.js';
import { createProductObject } from '../mappers/analytics/structured-data-mapper.js';
import { mapLineItemToDataLayerEvent } from '../mappers/analytics/data-layer/line-item-to-data-layer-event.js';
import { getCartRows } from '../mappers/analytics/monetate/get-cart-rows.js';
import { CREATE_CART } from '../operations/mutations/create-cart.js';
import { ADD_CART_LINE_ITEMS } from '../operations/mutations/add-cart-line-items.js';

const useAddToCart = ()=>{
    const intl = useIntl();
    const { locale , currency , country  } = intl;
    const settings = useCartSettings();
    const [cart, { setCartId , setCartCount , setCartHash  }] = useCartStorage();
    const { isMonetateProductDataEnabled , isPrescriptionRecognitionEnabled  } = useFeatureToggles();
    const [createCart, createCartMutation] = useMutation(CREATE_CART);
    const [addCartLineItems, addCartLineItemsMutation] = useMutation(ADD_CART_LINE_ITEMS);
    const handleOnCompleted = (addedLineItemDrafts, data, onError, onSuccess, onValidationError, onSolvableValidationError, pushRemoveLineItemDataLayerEvent)=>{
        let result;
        if ('addCartLineItems' in data) {
            result = data.addCartLineItems;
        }
        if ('createCart' in data) {
            result = data.createCart;
        }
        if (result.validationErrors) {
            const solvableValidationErrors = getSolvableValidationErrors(result.validationErrors);
            if (solvableValidationErrors.length > 0 && onSolvableValidationError) {
                onSolvableValidationError(solvableValidationErrors);
                return;
            }
            const validationErrors = getValidationErrorCodes(result.validationErrors);
            onValidationError && onValidationError(validationErrors);
            if (!result.success) {
                return;
            }
        }
        if (!result.cart) {
            return onError(errorsMapping.CART_IS_EMPTY);
        }
        const { cartHash  } = result;
        const { id , numberOfItems , lineItems  } = result.cart;
        setCartId(id);
        setCartCount(numberOfItems);
        if (cartHash) {
            setCartHash(cartHash);
        }
        const addedLineItems = getAddedLineItems(lineItems, addedLineItemDrafts);
        const addedLineItemsMapped = lineItemDataMapper(addedLineItems, settings, intl);
        const SdProductObjects = addedLineItemsMapped.map((lineItem)=>createProductObject(lineItem)
        );
        const dataLayerEvents = mapLineItemToDataLayerEvent(DataLayer.DataLayerTypes.EventType.add, addedLineItemsMapped, id);
        dataLayerEvents && DataLayer.pushEcommerceEvent(dataLayerEvents);
        pushRemoveLineItemDataLayerEvent && pushRemoveLineItemDataLayerEvent();
        dispatchMiniCartEvent('added', addedLineItemsMapped);
        CartV2Actions.addToCartSdAction(SdProductObjects);
        onSuccess && onSuccess(addedLineItemsMapped);
        monetate().trackCartContents(isMonetateProductDataEnabled ? getCartRows(lineItems, currency) : []);
    };
    const addToCart = React__default.useCallback(function() {
        var _ref = swcHelpers.asyncToGenerator(function*(lineItemDrafts, onSuccess, onValidationError, onError = ()=>undefined
        , onSolvableValidationError, sanitizationItems, pushRemoveLineItemDataLayerEvent, options = {}) {
            let lineItems = lineItemDrafts.map(mapDraftLineItemToServer).filter(isNotNull);
            if (!lineItems.length) {
                return onError(errorsMapping.SOMETHING_WENT_WRONG);
            }
            if (sanitizationItems) {
                lineItems = appendSanitizationItems(lineItems, sanitizationItems);
            }
            // the newPrescription should only be sent to server if the OCR FT is enabled
            if (!isPrescriptionRecognitionEnabled) {
                lineItems = lineItems.map((item)=>{
                    var ref;
                    if ((ref = item.typeDetails) === null || ref === void 0 ? void 0 : ref.prescriptionFile) {
                        delete item.typeDetails.newPrescription;
                    }
                    return item;
                });
            }
            /**
       * On the PDP, there will be multiple instances of this hook (useAddToCart)
       * running. This is causing a mismatch in the StoredCart data value that is returned
       * from localStorage. Pending rethinking how to fix this issue properly, we'll
       * let this hook itself get the StoredCart value (containing cartId) from localStorage
       * instead of relying on the useCartStorage hook (which might return an old StoredCart value).
       */ let localCartData = null;
            try {
                const localStorageCartData = window.localStorage.getItem(CART_STORAGE_KEY);
                localCartData = isNotNull(localStorageCartData) ? JSON.parse(localStorageCartData) : null;
            } catch (err) {
                console.error(`Failed retrieving cart data from localStorage. Error: ${err}`);
            }
            const { prescriptionSignatures  } = options;
            try {
                const { data , errors  } = yield isNotNull(localCartData) && isNotNull(localCartData.cartId) ? addCartLineItems({
                    variables: {
                        cartInput: {
                            cartId: localCartData.cartId,
                            lineItems
                        }
                    }
                }) : createCart({
                    variables: {
                        cartInput: {
                            locale,
                            currency,
                            country,
                            lineItems,
                            prescriptionSignatures
                        }
                    }
                });
                if (errors) {
                    onError(errorsMapping.SOMETHING_WENT_WRONG);
                }
                if (data) {
                    handleOnCompleted(lineItemDrafts, data, onError, onSuccess, onValidationError, onSolvableValidationError, pushRemoveLineItemDataLayerEvent);
                }
            } catch (error) {
                onError(errorsMapping.SOMETHING_WENT_WRONG);
            }
        });
        return function(lineItemDrafts, onSuccess, onValidationError) {
            return _ref.apply(this, arguments);
        };
    }(), [
        cart
    ]);
    const { called , loading , error , data: data1  } = cart.cartId ? addCartLineItemsMutation : createCartMutation;
    let newCart;
    if (data1 && 'createCart' in data1) {
        newCart = data1.createCart.cart;
    }
    if (data1 && 'addCartLineItems' in data1) {
        newCart = data1.addCartLineItems.cart;
    }
    return [
        addToCart,
        {
            called,
            loading,
            error,
            cart: newCart
        }
    ];
};

export { useAddToCart };
//# sourceMappingURL=use-add-to-cart.js.map
